import { section236GAmountFor, section236HAmountFor } from "./Utils";
import { toHundredths, formatMoney as formatTwoDecimalMoney } from "./money";

/**
 * A buyer's ledger: one row per invoice billed to them, and the period's totals.
 *
 * Each row carries the invoice's stored figures and the two withholding taxes
 * worked out on top of them:
 *
 *   Value Excl. ST           100,000.00   <- subTotal
 *   Sales Tax                 18,000.00   <- taxTotal
 *   Invoice Total            118,000.00   <- grandTotal
 *   Section 236G @ 0.1%          118.00   <- 236G rate x invoice total
 *   GSection 236H @ 0.5%         590.00   <- 236H rate x invoice total
 *   Total Receivable         118,708.00   <- invoice total + 236G + 236H
 *
 * Neither tax is part of the invoice total (neither is reported to FBR), so the
 * receivable is the only figure that adds them in.
 *
 * Everything is summed in whole paisa, so a long ledger's totals never pick up
 * floating-point drift. A cancelled invoice is still listed - the ledger is the
 * buyer's whole record - but counts towards none of the totals: cancelling it
 * un-sold it.
 */

const toPaisa = toHundredths;

// Rates are shown to two decimals at most, like the invoice forms take them.
const toRate = (n) => toPaisa(n) / 100;

/**
 * The invoice's Section 236G %.
 *
 * The form takes 236G as free text, so a stored rate can read "0.5", "0.5%" or
 * "0.5 %"; the first number in it is the rate. Anything without one is 0.
 */
export const section236GRateOf = (invoice) => {
  const match = String(invoice?.section236G ?? "").match(/\d*\.?\d+/);
  return match ? toRate(match[0]) : 0;
};

/**
 * The invoice's GSection 236H %, read the way the invoice PDFs read it, so the
 * ledger can never print a 236H figure the invoice itself does not.
 */
export const section236HRateOf = (invoice) => toRate(invoice?.GSection236H);

export const isCancelledInvoice = (invoice) =>
  String(invoice?.FBRInvoiceStatus ?? "").toLowerCase() === "cancel";

const STATUS_LABEL = {
  valid: "Valid",
  invalid: "Invalid",
  cancel: "Cancelled",
};

export const ledgerStatusLabel = (status) =>
  STATUS_LABEL[String(status ?? "").toLowerCase()] || status || "-";

const TOTAL_KEYS = [
  "valueExclST",
  "salesTax",
  "invoiceTotal",
  "section236G",
  "section236H",
  "totalReceivable",
];

/**
 * Rows in the order given (the server sends them oldest first) and the totals
 * over every invoice that is not cancelled.
 */
export const buildBuyerLedger = (invoices) => {
  const paisa = Object.fromEntries(TOTAL_KEYS.map((key) => [key, 0]));
  let invoiceCount = 0;
  let cancelledCount = 0;

  const rows = (Array.isArray(invoices) ? invoices : []).map(
    (invoice, index) => {
      const rate236G = section236GRateOf(invoice);
      const rate236H = section236HRateOf(invoice);

      const figures = {
        valueExclST: toPaisa(invoice?.subTotal),
        salesTax: toPaisa(invoice?.taxTotal),
        invoiceTotal: toPaisa(invoice?.grandTotal),
        section236G: toPaisa(
          section236GAmountFor(invoice?.grandTotal, rate236G),
        ),
        section236H: toPaisa(
          section236HAmountFor(invoice?.grandTotal, rate236H),
        ),
      };
      figures.totalReceivable =
        figures.invoiceTotal + figures.section236G + figures.section236H;

      const cancelled = isCancelledInvoice(invoice);
      if (cancelled) {
        cancelledCount += 1;
      } else {
        invoiceCount += 1;
        TOTAL_KEYS.forEach((key) => {
          paisa[key] += figures[key];
        });
      }

      return {
        sr: index + 1,
        id: invoice?._id,
        date: invoice?.date || "",
        invoiceNumber: invoice?.invoiceNumber || "",
        invoiceType: invoice?.invoiceType || "",
        FBRInvoiceRefNo: invoice?.FBRInvoiceRefNo || "",
        status: ledgerStatusLabel(invoice?.FBRInvoiceStatus),
        companyStatus: invoice?.companyStatus || "",
        companyName: invoice?.companyId?.companyName || "",
        cancelled,
        rate236G,
        rate236H,
        ...Object.fromEntries(
          TOTAL_KEYS.map((key) => [key, figures[key] / 100]),
        ),
      };
    },
  );

  return {
    rows,
    totals: Object.fromEntries(
      TOTAL_KEYS.map((key) => [key, paisa[key] / 100]),
    ),
    invoiceCount,
    cancelledCount,
  };
};

/** Money as the ledger prints it: thousands separated, always two decimals. */
export const formatMoney = formatTwoDecimalMoney;

/** A rate as the ledger prints it: "0.5%", or "-" when there is none. */
export const formatRate = (rate) => (Number(rate) > 0 ? `${rate}%` : "-");
